import React, { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import ModuleFrame, { Chip, KV, useModuleTab } from '../../components/ModuleFrame';
import { AppFocusChips, useSelectedApp, withApp } from '../../components/AppSelector';
import AskPane from '../../components/AskPane';
import { updateEntity, useEntityList, friendlyError } from '../../data/entities';
import { apiPost } from '../../utils/backendApi';
import { useAuth } from '../../AuthContext';
import { askNova } from '../../components/ConsoleHeader';
import { LibraryLifecycle } from '../../components/LifecycleRail';

// NoVA Experience — what the app's users meet. NoVA is one assistant on
// four channels (web, widget, voice, mobile) over the same guardrailed
// answer path. This module shows the channels, lets you talk to NoVA as a
// user would (with the answer context — citations, guardrails fired),
// hands a conversation to a human, and closes the loop with feedback on
// copilot answers.

// `state` is what the PLATFORM offers; whether THIS app has the channel is
// the separate "enabled for <app>" chip (its channels list).
const CHANNELS = (orgId, app) => [
  { id: 'web', label: 'Web', state: 'available', help: 'This console and every hosted instance: NoVA answers inside the app.', to: withApp(`/orgs/${orgId}/builder`, app) },
  { id: 'widget', label: 'Embeddable widget', state: 'available', help: 'One script tag on any site — the agent token carries the allowlist.', to: `/orgs/${orgId}/workflows?tab=agents` },
  { id: 'voice', label: 'Voice', state: 'available', help: 'Realtime voice through the voice agent; keys stay server-side; tools are allowlisted.', to: `/orgs/${orgId}/workflows?tab=agents` },
  { id: 'mobile', label: 'Mobile', state: 'shell only', help: 'Capacitor shell of the web experience; native features are wave 2.', to: null },
];

export default function NovaModule() {
  const { org } = useOutletContext();
  const { user } = useAuth();
  const { app } = useSelectedApp();
  const [tab, setTab] = useModuleTab('ask');
  const { docs: sessions } = useEntityList('aiSession', { org: org.id }, { orderBy: 'updatedAt', dir: 'desc' });
  const { docs: reviewItems } = useEntityList('reviewItem', { org: org.id });
  const [sessionId, setSessionId] = useState(null);
  const [msg, setMsg] = useState('');

  const handoffs = (reviewItems || []).filter((r) => r.source?.mode === 'handoff' || r.source?.mode === 'ask');
  const openHandoffs = handoffs.filter((r) => r.status === 'pending');
  const session = (sessions || []).find((s) => s.id === sessionId) || (sessions || [])[0] || null;
  const g = org.guardrails || {};
  // Where a handoff actually lands: the first routing rule that matches a
  // `handoff` reason (server-side, shared/reviewRouting.js), else the queue it
  // is filed in.
  const handoffRule = (org.reviewRouting?.rules || []).find((r) => r.match?.reason === 'handoff' && (!r.match.queue || r.match.queue === 'nova'));
  const handoffCopy = handoffRule?.set?.queue
    ? `Request human assistance files a review item; rule “${handoffRule.name}” routes it to the ${handoffRule.set.queue} queue${handoffRule.set.priority ? ` at ${handoffRule.set.priority} priority` : ''}`
    : 'Request human assistance files a review item in the nova queue';

  const handoff = async (question, answer) => {
    setMsg('');
    try {
      const out = await apiPost(`/review/${org.id}/handoff`, { content: `Q: ${question}\n\nA (NoVA): ${answer}`, appId: app?.id || null, queue: 'nova', reason: 'handoff' });
      setMsg(`Handed to Human Review (${out.queue} queue${out.assigneeUid ? ', assigned' : ''}).`);
    } catch (e) { setMsg(e.message); }
  };

  return (
    <ModuleFrame
      title="NoVA Experience"
      subtitle="Channels · Conversations · Answer context · Human handoff"
      accent="#f472b6"
      note={`NoVA Experience, tab ${tab}${app ? `, app "${app.name}"` : ', no app focused'}: ${openHandoffs.length} open handoff${openHandoffs.length === 1 ? '' : 's'}. NoVA: create_agent, update_agent (greeting, instructions, allowlist), ask_knowledge to test an answer; conversations are read on this screen.`}
      help="NoVA is the face of every app you build here and the copilot of this console. Talk to it the way a user would, see where each answer came from, and hand off to a person when it should not answer alone."
      context={(
        <>
          <AppFocusChips allowNone />
          <Chip tone={openHandoffs.length ? 'warn' : 'ok'}>{openHandoffs.length} open handoffs</Chip>
        </>
      )}
      lead={app ? <LibraryLifecycle org={org} app={app} moduleId="nova" /> : null}
      tabs={[
        { id: 'ask', label: 'Ask NoVA' },
        { id: 'channels', label: 'Channels' },
        { id: 'conversations', label: 'Recent conversations', count: (sessions || []).length },
        { id: 'controls', label: 'Experience controls' },
      ]}
      tab={tab}
      onTab={setTab}
      verbs={[
        { label: 'Ask as a user', hint: 'guardrailed answer path', onClick: () => setTab('ask'), primary: true },
        { label: 'Open the copilot', hint: 'NoVA · agentOS', onClick: () => askNova('') },
        { label: 'Open handoffs', hint: `${openHandoffs.length} waiting`, to: `/orgs/${org.id}/human-review` },
      ]}
    >
      {tab === 'ask' && (
        <ModuleFrame.Columns>
          <ModuleFrame.Panel title="Ask NoVA" meta={app ? `as a user of ${app.name}` : 'as a user of this org\'s apps'} className="mf-span-2">
            <AskPane orgId={org.id} appId={app?.id || null} onHandoff={handoff} />
            {msg && <p className={msg.startsWith('Handed') ? 'mf-muted' : 'cs-form-error'} style={{ marginTop: 8 }}>{msg}</p>}
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Answer context" meta="what every answer carries">
            <KV rows={[
              ['Citations', 'the published docs each answer drew from — shown as chips'],
              ['Guardrails fired', Object.entries(g).filter(([, v]) => v).map(([k]) => k).join(', ') || 'none configured'],
              ['Scope', app ? `knowledge linked to ${app.name} + org-wide docs` : 'all org knowledge'],
              ['Handoff', handoffCopy],
            ]} />
            <Link to={`/orgs/${org.id}/knowledge?tab=guardrails`} className="mf-muted">Tune guardrails →</Link>
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}

      {tab === 'channels' && (
        <div className="mf-grid-cards">
          {CHANNELS(org.id, app).map((c) => (
            <div key={c.id} className="mf-card">
              <div className="mf-card-title" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{c.label} <Chip tone="muted">{c.state}</Chip>{app && ((app.channels || []).includes(c.id) ? <Chip tone="ok">enabled for {app.name}</Chip> : <Chip tone="muted">not enabled for {app.name}</Chip>)}</div>
              <div className="mf-muted" style={{ flex: 1 }}>{c.help}</div>
              {c.to ? <Link to={c.to} className="cs-btn cs-btn-outline cs-btn-sm">Configure →</Link> : <span className="mf-muted">no configuration yet</span>}
            </div>
          ))}
        </div>
      )}

      {tab === 'conversations' && (
        <ModuleFrame.Columns>
          <ModuleFrame.Panel title="Sessions" meta="copilot conversations in this org">
            {!(sessions || []).length && <p className="cs-help">No conversations yet — open the copilot and ask something.</p>}
            <ul className="mf-list">
              {(sessions || []).slice(0, 40).map((s) => (
                <li key={s.id}><button type="button" className="mf-row" style={{ width: '100%', cursor: 'pointer', color: 'var(--text)', borderColor: session?.id === s.id ? 'rgba(244,114,182,0.5)' : undefined }} onClick={() => setSessionId(s.id)}>
                  <Chip tone="muted">{s.channel || 'web'}</Chip>
                  <span className="mf-row-grow" style={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title || 'Session'}</span>
                  <span className="mf-muted">{s.updatedAt ? new Date(s.updatedAt).toLocaleDateString() : ''}</span>
                </button></li>
              ))}
            </ul>
          </ModuleFrame.Panel>
          <div className="mf-span-2">{session && <Transcript orgId={org.id} session={session} uid={user.uid} />}</div>
        </ModuleFrame.Columns>
      )}

      {tab === 'controls' && (
        <ModuleFrame.Columns cols={2}>
          <ModuleFrame.Panel title="Experience controls" meta="enforced in the answer path">
            <ul className="mf-list">
              {[['approvedSourcesOnly', 'Approved sources only'], ['citationsRequired', 'Citations required'], ['sensitiveDataMasking', 'Sensitive data masking'], ['uncertainToReview', 'Uncertain → human']].map(([k, label]) => (
                <li key={k} className="mf-row"><Chip tone={g[k] ? 'ok' : 'muted'}>{g[k] ? 'on' : 'off'}</Chip><span className="mf-row-grow">{label}</span></li>
              ))}
            </ul>
            <Link to={`/orgs/${org.id}/knowledge?tab=guardrails`} className="cs-btn cs-btn-outline cs-btn-sm" style={{ marginTop: 10 }}>Change in Knowledge → Guardrails</Link>
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Handoff history" meta={`${handoffs.length} items`}>
            <ul className="mf-list">
              {handoffs.slice(0, 20).map((r) => (
                <li key={r.id} className="mf-row">
                  <Chip tone={r.status === 'pending' ? 'warn' : r.status === 'approved' ? 'ok' : 'muted'}>{r.status}</Chip>
                  <span className="mf-row-grow" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(r.content || '').slice(0, 100)}</span>
                  <Link to={`/orgs/${org.id}/human-review?item=${r.id}`} className="mf-muted">open →</Link>
                </li>
              ))}
            </ul>
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}
    </ModuleFrame>
  );
}

function Transcript({ orgId, session, uid }) {
  const { docs: messages } = useEntityList('aiMessage', { org: orgId, aiSession: session.id }, { orderBy: 'createdAt', dir: 'asc' });
  const [err, setErr] = useState('');
  const rate = async (m, rating) => {
    setErr('');
    try { await updateEntity('aiMessage', { org: orgId, aiSession: session.id }, m.id, { feedback: { rating, uid, at: Date.now() } }); } catch (e) { setErr(friendlyError(e)); }
  };
  return (
    <ModuleFrame.Panel title={session.title || 'Session'} meta={`${(messages || []).length} messages · ${session.channel || 'web'}`}>
      <div style={{ display: 'grid', gap: 8 }}>
        {(messages || []).map((m) => (
          <div key={m.id} className="mf-row" style={{ alignItems: 'flex-start', flexDirection: 'column', gap: 4, background: m.role === 'user' ? 'rgba(var(--fg-rgb),0.03)' : 'rgba(244,114,182,0.05)' }}>
            <div className="mf-muted">{m.role === 'user' ? 'user' : 'NoVA'}{m.status && m.status !== 'none' ? ` · ${m.status}` : ''}</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>
            {m.role === 'assistant' && (
              <div style={{ display: 'flex', gap: 4 }}>
                <button type="button" className={`cs-btn cs-btn-sm ${m.feedback?.rating === 'up' ? 'cs-btn-primary' : 'cs-btn-ghost'}`} onClick={() => rate(m, 'up')} aria-label="helpful">👍</button>
                <button type="button" className={`cs-btn cs-btn-sm ${m.feedback?.rating === 'down' ? 'cs-btn-primary' : 'cs-btn-ghost'}`} onClick={() => rate(m, 'down')} aria-label="not helpful">👎</button>
              </div>
            )}
          </div>
        ))}
      </div>
      {err && <p className="cs-form-error" role="alert">{err}</p>}
    </ModuleFrame.Panel>
  );
}
